import {
  scheduleWindowCoversInstant,
  type RoutingSkuTargetRecord,
  type RoutingTimeWindowRecord,
} from '@nadoutong/contracts';

/**
 * One SKU's day, cut into the stretches where a different schedule is in
 * force (0044).
 *
 * The page has to answer two questions at a glance — "who serves this right
 * now" and "who serves it at 03:00" — and neither is answerable from a list of
 * windows: an operator would have to convert timezones, apply the midnight
 * wrap, and subtract the covered stretches from the day in their head.
 *
 * Two deliberate choices:
 *
 *  1. **The axis is a real day, evaluated at real instants.** Every minute of
 *     the displayed day is a `Date`, tested against each window with the
 *     shared `scheduleWindowCoversInstant` — the same week×minute expansion
 *     the write face uses to reject overlaps. Drawing the axis in "the
 *     window's local hours" instead would silently lie whenever two windows
 *     carry different timezones, which the primitive allows.
 *
 *  2. **A window that does not touch this SKU is not on this SKU's timeline.**
 *     A group may override legs across many SKUs; showing one here because it
 *     exists would tell an operator that this model's routing changes at
 *     22:00 when it does not.
 */

/** Minutes per rendered day. DST days are still drawn as 24 slots. */
export const TIMELINE_MINUTES = 1440;

export interface TimelineLeg {
  readonly targetId: string;
  /** Effective weight in this segment: the override, else the base row. */
  readonly weight: number;
  /** Effective retry position, same fallback. */
  readonly retryOrder: number | null;
  /** True when this segment's value came from the window, not the base row. */
  readonly overridden: boolean;
}

export interface TimelineSegment {
  /** `null` = the uncovered stretch, which serves the standard pool. */
  readonly window: RoutingTimeWindowRecord | null;
  /** Inclusive minute-of-day where this stretch starts. */
  readonly startMinute: number;
  /** Exclusive minute-of-day where it ends. */
  readonly endMinute: number;
  /** True for the stretch containing the instant the page was rendered at. */
  readonly current: boolean;
  readonly legs: readonly TimelineLeg[];
}

/**
 * Windows that actually move this SKU: at least one override names a target of
 * this SKU's pool.
 */
export function windowsForSku(
  windows: readonly RoutingTimeWindowRecord[],
  targets: readonly RoutingSkuTargetRecord[],
): readonly RoutingTimeWindowRecord[] {
  const targetIds = new Set(targets.map((target) => target.id));
  return windows.filter((window) =>
    window.enabled && window.overrides.some((override) => targetIds.has(override.target_id)));
}

/** Effective legs of this SKU under `window` (or under no window at all). */
export function legsUnderWindow(
  targets: readonly RoutingSkuTargetRecord[],
  window: RoutingTimeWindowRecord | null,
): readonly TimelineLeg[] {
  const overrides = new Map(
    (window?.overrides ?? []).map((override) => [override.target_id, override]),
  );
  return targets
    .filter((target) => target.enabled)
    .map((target) => {
      const override = overrides.get(target.id);
      return {
        targetId: target.id,
        weight: override?.weight ?? target.weight,
        retryOrder: override?.retry_order ?? target.retry_order,
        // A window row that only sets `retry_order` has not overridden the
        // weight, and the badge must not claim it has.
        overridden: override !== undefined,
      };
    });
}

/**
 * Start of the day containing `now` in the operator's own timezone.
 *
 * The axis is the operator's clock on purpose: "现在走谁" is a question about
 * the wall clock they are reading, and a window's own zone is shown per row
 * rather than imposed on the axis.
 */
export function startOfLocalDay(now: Date): Date {
  const start = new Date(now.getTime());
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Cut the displayed day into stretches, one per in-force window plus the
 * uncovered remainder.
 *
 * Evaluated minute by minute rather than by projecting each window's local
 * hours onto the axis: with per-window timezones, a DST transition, or a
 * window whose weekday mask excludes today, projection is wrong in ways that
 * are invisible until an incident. 1440 membership tests per window is a
 * rounding error next to one render.
 *
 * Overlapping windows cannot occur — the write face refuses them — but if one
 * ever did, the first match wins, matching the SQL's `ORDER BY window_id ASC
 * LIMIT 1` tie-break in spirit: a deterministic answer rather than a blend.
 */
export function dayTimelineSegments(params: {
  readonly targets: readonly RoutingSkuTargetRecord[];
  readonly windows: readonly RoutingTimeWindowRecord[];
  readonly now: Date;
}): readonly TimelineSegment[] {
  const applicable = [...windowsForSku(params.windows, params.targets)]
    .sort((left, right) => left.id.localeCompare(right.id));
  const dayStart = startOfLocalDay(params.now);
  const nowMinute = Math.floor((params.now.getTime() - dayStart.getTime()) / 60_000);

  const owner = new Array<RoutingTimeWindowRecord | null>(TIMELINE_MINUTES).fill(null);
  for (let minute = 0; minute < TIMELINE_MINUTES; minute += 1) {
    const instant = new Date(dayStart.getTime() + minute * 60_000);
    owner[minute] = applicable
      .find((window) => scheduleWindowCoversInstant(window, instant)) ?? null;
  }

  const segments: TimelineSegment[] = [];
  let cursor = 0;
  while (cursor < TIMELINE_MINUTES) {
    const window = owner[cursor] ?? null;
    let end = cursor + 1;
    while (end < TIMELINE_MINUTES && (owner[end] ?? null) === window) end += 1;
    segments.push({
      window,
      startMinute: cursor,
      endMinute: end,
      current: nowMinute >= cursor && nowMinute < end,
      legs: legsUnderWindow(params.targets, window),
    });
    cursor = end;
  }
  return segments;
}

/** `07:30` from a minute-of-day. */
export function formatMinuteOfDay(minute: number): string {
  const normalized = ((minute % TIMELINE_MINUTES) + TIMELINE_MINUTES) % TIMELINE_MINUTES;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Weekday-mask bits, Monday first, as a compact `一二三…` style index list. */
export function weekdayIndexes(daysOfWeek: number): readonly number[] {
  const days: number[] = [];
  for (let bit = 0; bit < 7; bit += 1) {
    if (((daysOfWeek >> bit) & 1) === 1) days.push(bit);
  }
  return days;
}
